/**
 * Environment Library Modal
 *
 * First-class reusable creative asset: a persistent location that can be
 * reused across videos and scenes. Users create environments with 1–3
 * reference images, browse them in the library, and open/edit them.
 */

import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { toastMessage } from '../../i18n/toastMessages';
import '../characterlibrary/CharacterLibrary.css';
import './EnvironmentLibrary.css';
import { Environment, MAX_ENVIRONMENT_REFERENCE_IMAGES, MAX_ENVIRONMENT_IMAGE_LABEL_LENGTH } from '../../types/EnvironmentTypes';
import {
  hydrateEnvironmentsForCurrentUser,
  deleteEnvironment,
  saveEnvironment,
  createNewEnvironment,
  buildEnvironmentReferenceImage,
} from '../../services/environmentService';
import {
  getUploadURI,
  uploadCharacterImage,
  useCharacterImageUrl,
} from '../../services/characterStorageService';
import {
  isImageAspectRatioWithinBounds,
  LIBRARY_IMAGE_ASPECT_BOUNDS,
} from '../../services/videoImageLimits';
import EnvironmentCard from './EnvironmentCard';
import { Close, Upload, Add, Collections } from '@mui/icons-material';

interface EnvironmentLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSelectEnvironment?: (environment: Environment) => void;
  /**
   * When provided, the library jumps straight into the editor for that
   * environment on open.
   */
  focusEnvironmentId?: string;
  /**
   * Bumped by the parent whenever the library closes, so pickers elsewhere
   * can refresh. Not consumed here — parents watch close events.
   */
  onLibraryChange?: () => void;
}

type ViewMode = 'library' | 'create' | 'edit';

function isSupportedEnvironmentImage(file: File): boolean {
  const normalizedType = file.type.toLowerCase();
  const normalizedName = file.name.toLowerCase();

  return (
    normalizedType === 'image/png' ||
    normalizedType === 'image/jpeg' ||
    normalizedType === 'image/webp' ||
    normalizedName.endsWith('.png') ||
    normalizedName.endsWith('.jpg') ||
    normalizedName.endsWith('.jpeg') ||
    normalizedName.endsWith('.webp')
  );
}

/**
 * Reads an image file and resolves with its natural pixel dimensions. Used
 * when building the reference-image entry after the file is persisted.
 */
function readImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () =>
        reject(new Error('Failed to read image dimensions'));
      img.src = dataUrl;
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Thumbnail for one staged reference image, with an editable label.
 * Stored blob paths are signed with the user SAS token; direct URLs
 * render as-is.
 */
const ReferenceThumb: React.FC<{
  url: string;
  name: string;
  maxLabelLength: number;
  onLabelChange: (label: string) => void;
  onRemove: () => void;
  disabled: boolean;
}> = ({ url, name, maxLabelLength, onLabelChange, onRemove, disabled }) => {
  const src = useCharacterImageUrl(url);
  return (
    <div className="environment-reference-thumb">
      {src ? (
        <img src={src} alt={name} />
      ) : (
        <div className="environment-reference-thumb-loading">Loading…</div>
      )}
      <button
        type="button"
        className="environment-reference-remove"
        onClick={onRemove}
        disabled={disabled}
        title={`Remove ${name}`}
        aria-label={`Remove ${name}`}
      >
        ×
      </button>
      <input
        type="text"
        className="environment-reference-label environment-reference-label-overlay"
        value={name}
        maxLength={maxLabelLength}
        onChange={(e) => onLabelChange(e.target.value)}
        disabled={disabled}
        placeholder="Label this view…"
        title="Label for this reference image (e.g. Wide angle)"
        aria-label="Reference image label"
      />
    </div>
  );
};

const EnvironmentLibrary: React.FC<EnvironmentLibraryProps> = ({
  isOpen,
  onClose,
  onSelectEnvironment,
  focusEnvironmentId,
  onLibraryChange,
}) => {
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('library');
  const [selectedEnvironment, setSelectedEnvironment] =
    useState<Environment | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftImages, setDraftImages] = useState<
    { id: string; url: string; name: string; width: number; height: number }[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const loadEnvironments = async () => {
    const hydrated = await hydrateEnvironmentsForCurrentUser();
    setEnvironments(hydrated);
    return hydrated;
  };

  useEffect(() => {
    if (!isOpen) return;
    void loadEnvironments().then((loaded) => {
      if (focusEnvironmentId) {
        const focused = loaded.find((env) => env.id === focusEnvironmentId);
        if (focused) {
          openEditor(focused);
          return;
        }
      }
      setViewMode('library');
      setSelectedEnvironment(null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const resetDrafts = () => {
    setDraftName('');
    setDraftDescription('');
    setDraftImages([]);
  };

  const startCreation = () => {
    resetDrafts();
    setSelectedEnvironment(null);
    setViewMode('create');
  };

  const openEditor = (environment: Environment) => {
    setSelectedEnvironment(environment);
    setDraftName(environment.name);
    setDraftDescription(environment.description ?? '');
    setDraftImages(environment.referenceImages.map((img) => ({ ...img })));
    setViewMode('edit');
  };

  const handleCancelStudio = () => {
    resetDrafts();
    setSelectedEnvironment(null);
    setViewMode('library');
  };

  const handleDelete = async (id: string) => {
    const target = environments.find((env) => env.id === id);
    if (
      !window.confirm(
        `Delete "${target?.name ?? 'this environment'}"? This cannot be undone.`
      )
    ) {
      return;
    }
    const ok = await deleteEnvironment(id);
    if (ok) {
      const refreshed = await loadEnvironments();
      setEnvironments(refreshed);
      onLibraryChange?.();
      toast.success(toastMessage('environment.deleted'));
    } else {
      toast.error(toastMessage('environment.deleteFailed'));
    }
  };

  const handleFilesSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;

    const remainingSlots = MAX_ENVIRONMENT_REFERENCE_IMAGES - draftImages.length;
    if (remainingSlots <= 0) {
      toast.error(
        toastMessage('environment.maxImages', {
          max: MAX_ENVIRONMENT_REFERENCE_IMAGES,
        })
      );
      return;
    }
    const accepted = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      toast.info(
        toastMessage('environment.onlyFirstImages', {
          slots: remainingSlots,
          max: MAX_ENVIRONMENT_REFERENCE_IMAGES,
        })
      );
    }

    for (const file of accepted) {
      if (!isSupportedEnvironmentImage(file)) {
        toast.error(
          toastMessage('environment.unsupportedImage', { file: file.name })
        );
        continue;
      }
    }
    const supported = accepted.filter(isSupportedEnvironmentImage);
    if (supported.length === 0) return;

    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error(toastMessage('common.loginRequired'));
      return;
    }

    setIsUploading(true);
    try {
      for (const file of supported) {
        const { width, height } = await readImageDimensions(file);
        if (
          !isImageAspectRatioWithinBounds(
            width,
            height,
            LIBRARY_IMAGE_ASPECT_BOUNDS
          )
        ) {
          toast.error(
            toastMessage('environment.aspectRatio', {
              file: file.name,
              ratio: width / height,
              min: LIBRARY_IMAGE_ASPECT_BOUNDS.min,
              max: LIBRARY_IMAGE_ASPECT_BOUNDS.max,
            })
          );
          continue;
        }
        const { uploadUrl, location } = await getUploadURI(file.name, token);
        await uploadCharacterImage(file, uploadUrl);
        setDraftImages((prev) =>
          prev.length >= MAX_ENVIRONMENT_REFERENCE_IMAGES
            ? prev
            : [
                ...prev,
                buildEnvironmentReferenceImage(location, file.name, width, height),
              ]
        );
      }
    } catch (error: any) {
      toast.error(
        error?.message || toastMessage('environment.uploadFailed')
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveDraftImage = (id: string) => {
    setDraftImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handleDraftImageLabelChange = (id: string, label: string) => {
    setDraftImages((prev) =>
      prev.map((img) =>
        img.id === id
          ? { ...img, name: label.slice(0, MAX_ENVIRONMENT_IMAGE_LABEL_LENGTH) }
          : img
      )
    );
  };

  const canSave =
    draftName.trim().length > 0 &&
    draftImages.length > 0 &&
    !isUploading &&
    !isSaving;

  const handleSave = async () => {
    if (!draftName.trim()) {
      toast.error(toastMessage('environment.nameRequired'));
      return;
    }
    if (draftImages.length === 0) {
      toast.error(toastMessage('environment.imageRequired'));
      return;
    }
    setIsSaving(true);
    try {
      let saved: Environment;
      if (viewMode === 'edit' && selectedEnvironment) {
        saved = await saveEnvironment({
          ...selectedEnvironment,
          name: draftName.trim(),
          description: draftDescription.trim(),
          identity: {
            ...selectedEnvironment.identity,
            name: draftName.trim(),
            description: draftDescription.trim(),
          },
          referenceImages: draftImages,
          updatedAt: new Date().toISOString(),
        });
        toast.success(toastMessage('environment.updated', { name: saved.name }));
      } else {
        const created = createNewEnvironment(
          draftName.trim(),
          draftDescription.trim(),
          draftImages
        );
        saved = await saveEnvironment(created);
        toast.success(toastMessage('environment.created', { name: saved.name }));
      }
      const refreshed = await loadEnvironments();
      setEnvironments(refreshed);
      onLibraryChange?.();
      resetDrafts();
      setSelectedEnvironment(null);
      setViewMode('library');
    } catch (error: any) {
      toast.error(
        error?.message || toastMessage('environment.saveFailed')
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const showAddTile =
    draftImages.length < MAX_ENVIRONMENT_REFERENCE_IMAGES;

  return (
    <div className="character-library-overlay" onClick={onClose}>
      <div
        className="character-library-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="character-library-header">
          <div className="character-library-header-copy">
            <span className="studio-kicker">Environment Studio</span>
            <h2>
              {viewMode === 'library' && 'Environments'}
              {viewMode === 'create' && 'Create Environment'}
              {viewMode === 'edit' && 'Edit Environment'}
            </h2>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <Close />
          </button>
        </div>

        {/* Content */}
        <div className="character-library-content">
          {viewMode === 'library' && (
            <>
              <section className="studio-hero">
                <div className="studio-hero-copy">
                  <span className="studio-hero-tag">Reusable locations</span>
                  <h3>
                    Build persistent locations for your stories and videos.
                  </h3>
                  <p>
                    Attach 1–3 reference images so Pixle keeps the location
                    consistent wherever it appears.
                  </p>
                  <div className="studio-hero-actions">
                    <button
                      className="studio-action primary"
                      onClick={startCreation}
                    >
                      <Add style={{ fontSize: 18 }} />
                      Create Environment
                    </button>
                  </div>
                </div>
                <div className="studio-hero-stats">
                  <div className="studio-stat">
                    <span>{environments.length}</span>
                    <label>
                      {environments.length === 1
                        ? 'Environment'
                        : 'Environments'}
                    </label>
                  </div>
                </div>
              </section>

              <div className="studio-section-header">
                <div>
                  <span className="studio-section-eyebrow">
                    Your environment library
                  </span>
                  <h3>Open and refine existing environments</h3>
                </div>
              </div>

              <div className="environment-grid">
                {environments.length === 0 ? (
                  <div className="empty-state">
                    <Collections style={{ fontSize: 48, opacity: 0.5 }} />
                    <p>No environments yet</p>
                    <p>
                      Create reusable environments from reference images to
                      keep your locations consistent across videos. Use
                      “Create Environment” above to add your first one.
                    </p>
                  </div>
                ) : (
                  environments.map((environment) => (
                    <EnvironmentCard
                      key={environment.id}
                      environment={environment}
                      onOpen={() => openEditor(environment)}
                      onDelete={() => handleDelete(environment.id)}
                      onSelect={
                        onSelectEnvironment
                          ? () => {
                              onSelectEnvironment(environment);
                              onClose();
                            }
                          : undefined
                      }
                    />
                  ))
                )}
              </div>
            </>
          )}

          {(viewMode === 'create' || viewMode === 'edit') && (
            <div className="environment-studio">
              <div className="studio-field">
                <label htmlFor="environment-name">Environment Name</label>
                <input
                  id="environment-name"
                  type="text"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder="e.g. Milo's Bedroom"
                />
              </div>

              <div className="studio-field">
                <label htmlFor="environment-description">Description</label>
                <textarea
                  id="environment-description"
                  value={draftDescription}
                  onChange={(e) => setDraftDescription(e.target.value)}
                  placeholder="A cozy bedroom where Milo lives..."
                />
              </div>

              <div className="studio-field">
                <label>Environment References</label>
                <p className="environment-references-copy">
                  Upload up to 3 images of the same environment. Different
                  views can help Pixle understand the location&apos;s
                  appearance and layout.
                </p>
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  multiple
                  onChange={handleFilesSelected}
                  disabled={isUploading || isSaving || !showAddTile}
                  style={{ display: 'none' }}
                />
                <div className="environment-references-grid">
                  {draftImages.map((img) => (
                    <ReferenceThumb
                      key={img.id}
                      url={img.url}
                      name={img.name}
                      maxLabelLength={MAX_ENVIRONMENT_IMAGE_LABEL_LENGTH}
                      onLabelChange={(label) =>
                        handleDraftImageLabelChange(img.id, label)
                      }
                      onRemove={() => handleRemoveDraftImage(img.id)}
                      disabled={isUploading || isSaving}
                    />
                  ))}
                  {showAddTile && (
                    <button
                      type="button"
                      className="environment-reference-add"
                      onClick={() => uploadInputRef.current?.click()}
                      disabled={isUploading || isSaving}
                      title={
                        draftImages.length === 0
                          ? 'Upload a reference image'
                          : 'Add another image'
                      }
                    >
                      <Upload style={{ fontSize: 22 }} />
                      <span>
                        {isUploading
                          ? 'Uploading…'
                          : draftImages.length === 0
                            ? 'Upload Image'
                            : 'Add another image'}
                      </span>
                    </button>
                  )}
                </div>
                <span className="studio-field-hint">
                  {draftImages.length} of {MAX_ENVIRONMENT_REFERENCE_IMAGES}{' '}
                  reference images · minimum 1, maximum{' '}
                  {MAX_ENVIRONMENT_REFERENCE_IMAGES}
                </span>
              </div>

              <div className="action-buttons environment-studio-actions">
                <button
                  type="button"
                  className="action-btn cancel"
                  onClick={handleCancelStudio}
                  disabled={isUploading || isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="action-btn apply"
                  onClick={handleSave}
                  disabled={!canSave}
                  title={
                    !draftName.trim()
                      ? 'Give your environment a name first'
                      : draftImages.length === 0
                        ? 'Upload at least one reference image first'
                        : undefined
                  }
                >
                  {isSaving
                    ? 'Saving…'
                    : viewMode === 'edit'
                      ? 'Save Environment'
                      : 'Create Environment'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnvironmentLibrary;
